<template>
  <div class="gallery-container">
    <!-- Полноэкранный просмотр -->
    <div v-if="fullscreenModel" class="fullscreen-view" :class="{ 'active': fullscreenModel }">
      <div class="fullscreen-header">
        <h2>{{ fullscreenModel.name }}</h2>
        <p class="model-description">{{ fullscreenModel.description }}</p>
        <button @click="closeFullscreen" class="close-btn">
          <span class="close-icon">×</span>
          Закрыть (ESC)
        </button>
      </div>
      <div class="fullscreen-content">
        <canvas ref="fullscreenCanvas" class="fullscreen-canvas"></canvas>
        <div class="fullscreen-controls">
          <button @click="toggleAutoRotate" class="control-btn">
            {{ autoRotate ? '⏸️' : '▶️' }} Автоповорот
          </button>
          <button @click="resetCamera" class="control-btn">🔄 Сбросить вид</button>
          <button @click="toggleWireframe" class="control-btn">
            {{ wireframe ? '🟦' : '⬜' }} Каркас
          </button>
        </div>
      </div>
    </div>

    <!-- Галерея -->
    <div class="gallery-header">
      <h1>3D Галерея</h1>
      <p class="gallery-subtitle">Нажмите на модель для полноэкранного просмотра</p>
      <div class="gallery-stats">
        <span>Моделей: {{ models.length }}</span>
        <span>Загружено: {{ loadedModelsCount }}/{{ models.length }}</span>
      </div>
    </div>

    <div class="gallery-grid">
      <div 
        v-for="model in models" 
        :key="model.id"
        class="model-card"
        @click="openFullscreen(model)"
        :class="{ 'loading': model.loading, 'error': model.error }"
      >
        <div class="model-thumbnail">
          <canvas :ref="el => setCanvasRef(model.id, el)" class="model-canvas"></canvas>
          
          <!-- Состояние загрузки -->
          <div v-if="model.loading" class="thumbnail-overlay loading">
            <div class="spinner"></div>
            <p>Загрузка...</p>
          </div>
          
          <!-- Ошибка -->
          <div v-if="model.error" class="thumbnail-overlay error">
            <p>❌ Ошибка</p>
            <small>{{ model.error }}</small>
          </div>
          
          <!-- Успешная загрузка -->
          <div v-else class="thumbnail-overlay info">
            <div class="model-badge">
              <span class="poly-count">▲ {{ model.polygons }}</span>
              <span v-if="model.hasAnimations" class="anim-badge">🎬</span>
            </div>
          </div>
        </div>
        
        <div class="model-card-info">
          <h3>{{ model.name }}</h3>
          <p class="model-description">{{ model.description || 'Без описания' }}</p>
          <div class="model-meta">
            <span class="file-name">{{ model.file }}</span>
            <span class="click-hint">👆 Нажмите для просмотра</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Информация о загрузке -->
    <div v-if="loadingProgress < 100" class="loading-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
      </div>
      <p>Загрузка моделей: {{ loadingProgress.toFixed(0) }}%</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Ссылки
const fullscreenCanvas = ref(null);
const canvasRefs = ref({});

// Состояние
const models = ref([]);
const fullscreenModel = ref(null);
const autoRotate = ref(true);
const wireframe = ref(false);

// Three.js объекты для полноэкранного режима
let fullscreenScene, fullscreenCamera, fullscreenRenderer, fullscreenControls;
let fullscreenMixer, fullscreenClock;

// Загружаем конфигурацию из JSON
const loadModelsConfig = async () => {
  try {
    const response = await fetch('/models.json');
    const config = await response.json();
    
    // Инициализируем модели с дополнительными полями
    models.value = config.models.map(model => ({
      ...model,
      loading: false,
      error: null,
      polygons: 0,
      hasAnimations: false,
      scene: null,
      camera: null,
      renderer: null,
      threeModel: null
    }));
    
    // Загружаем все модели
    loadAllModels();
  } catch (error) {
    console.error('Ошибка загрузки models.json:', error);
    // Запасные модели
    models.value = [
      {
        id: 1,
        name: 'Тигр',
        file: 'tiger2.fbx',
        description: 'Модель тигра',
        scale: 1.0,
        position: { x: 0, y: 0, z: 0 }
      },
      {
        id: 2,
        name: 'Танцующий волк',
        file: 'volk Silly Dancing.fbx',
        description: 'Волк выполняет забавный танец',
        scale: 0.8,
        position: { x: 0, y: 0.5, z: 0 }
      }
    ];
  }
};

// Прогресс загрузки
const loadingProgress = computed(() => {
  if (models.value.length === 0) return 0;
  const loaded = models.value.filter(m => !m.loading && !m.error).length;
  return (loaded / models.value.length) * 100;
});

const loadedModelsCount = computed(() => {
  return models.value.filter(m => !m.loading && !m.error).length;
});

// Загрузка всех моделей для миниатюр
const loadAllModels = () => {
  models.value.forEach(model => {
    loadModelForThumbnail(model);
  });
};

// Загрузка модели для миниатюры
const loadModelForThumbnail = async (model) => {
  const canvas = canvasRefs.value[model.id];
  if (!canvas) return;

  try {
    model.loading = true;
    model.error = null;

    // Создаем сцену
    model.scene = new THREE.Scene();
    model.scene.background = new THREE.Color(0x1a1a2e);

    // Камера
    model.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    model.camera.position.set(3, 2, 3);
    model.camera.lookAt(0, 0, 0);

    // Рендерер
    model.renderer = new THREE.WebGLRenderer({ 
      canvas,
      antialias: true,
      alpha: true
    });
    model.renderer.setSize(300, 300);
    model.renderer.setPixelRatio(1);

    // Освещение
    const ambientLight = new THREE.AmbientLight(0x00ffff, 0.6);
    model.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(2, 4, 3);
    model.scene.add(directionalLight);

    // Загружаем FBX
    const loader = new FBXLoader();
    const fbx = await new Promise((resolve, reject) => {
      loader.load(`/models/${model.file}`, resolve, null, reject);
    });

    // Обрабатываем модель
    fbx.traverse((child) => {
      if (child.isMesh) {
        // Упрощенный материал для миниатюр
        child.material = new THREE.MeshStandardMaterial({
          color: 0x4a9eff,
          metalness: 0.2,
          roughness: 0.7
        });
        
        // Считаем полигоны
        if (child.geometry) {
          if (child.geometry.index) {
            model.polygons += child.geometry.index.count / 3;
          } else if (child.geometry.attributes.position) {
            model.polygons += child.geometry.attributes.position.count / 3;
          }
        }
      }
    });

    // Проверяем анимации
    model.hasAnimations = fbx.animations && fbx.animations.length > 0;

    // Центрируем и масштабируем
    const box = new THREE.Box3().setFromObject(fbx);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    fbx.position.x = -center.x;
    fbx.position.y = -center.y + (size.y / 2);
    fbx.position.z = -center.z;

    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = (model.scale || 1.5) / maxDim;
    fbx.scale.multiplyScalar(scale);

    // Позиционируем
    if (model.position) {
      fbx.position.x += model.position.x;
      fbx.position.y += model.position.y;
      fbx.position.z += model.position.z;
    }

    model.scene.add(fbx);
    model.threeModel = fbx;

    // Запускаем анимацию миниатюры
    animateThumbnail(model);

  } catch (error) {
    console.error(`Ошибка загрузки ${model.name}:`, error);
    model.error = error.message;
  } finally {
    model.loading = false;
  }
};

// Анимация миниатюры
const animateThumbnail = (model) => {
  const animate = () => {
    if (!model.scene || !model.camera || !model.renderer) return;
    if (model.error) return;

    requestAnimationFrame(animate);

    // Вращение модели
    if (model.threeModel) {
      model.threeModel.rotation.y += 0.01;
    }

    model.renderer.render(model.scene, model.camera);
  };

  animate();
};

// Установка ссылки на canvas
const setCanvasRef = (id, el) => {
  if (el) {
    canvasRefs.value[id] = el;
  }
};

// Открыть полноэкранный режим
const openFullscreen = async (model) => {
  fullscreenModel.value = model;
  
  // Даем время на отрисовку DOM
  await nextTick();
  
  initFullscreenView();
  loadModelForFullscreen(model);
};

// Инициализация полноэкранного режима
const initFullscreenView = () => {
  if (!fullscreenCanvas.value) return;

  // Очищаем предыдущую сцену
  disposeFullscreen();

  // Создаем сцену
  fullscreenScene = new THREE.Scene();
  fullscreenScene.background = new THREE.Color(0x0a0a14);
  fullscreenScene.fog = new THREE.Fog(0x0a0a14, 10, 100);

  // Камера
  fullscreenCamera = new THREE.PerspectiveCamera(
    60,
    fullscreenCanvas.value.clientWidth / fullscreenCanvas.value.clientHeight,
    0.1,
    1000
  );
  fullscreenCamera.position.set(5, 3, 5);

  // Рендерер
  fullscreenRenderer = new THREE.WebGLRenderer({
    canvas: fullscreenCanvas.value,
    antialias: true,
    alpha: true
  });
  fullscreenRenderer.setSize(
    fullscreenCanvas.value.clientWidth,
    fullscreenCanvas.value.clientHeight
  );
  fullscreenRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  fullscreenRenderer.shadowMap.enabled = true;
  fullscreenRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
  fullscreenRenderer.outputEncoding = THREE.sRGBEncoding;
  fullscreenRenderer.toneMapping = THREE.ACESFilmicToneMapping;

  // Controls
  fullscreenControls = new OrbitControls(fullscreenCamera, fullscreenRenderer.domElement);
  fullscreenControls.enableDamping = true;
  fullscreenControls.dampingFactor = 0.05;
  fullscreenControls.autoRotate = autoRotate.value;

  // Освещение
  const ambientLight = new THREE.AmbientLight(0x00ffff, 0.5);
  fullscreenScene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
  directionalLight.position.set(10, 20, 15);
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.width = 2048;
  directionalLight.shadow.mapSize.height = 2048;
  fullscreenScene.add(directionalLight);

  const hemisphereLight = new THREE.HemisphereLight(0x443333, 0x222233, 0.5);
  fullscreenScene.add(hemisphereLight);

  // Пол
  const gridHelper = new THREE.GridHelper(20, 20, 0x444444, 0x222222);
  fullscreenScene.add(gridHelper);

  const planeGeometry = new THREE.PlaneGeometry(50, 50);
  const planeMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x222222,
    side: THREE.DoubleSide
  });
  const plane = new THREE.Mesh(planeGeometry, planeMaterial);
  plane.rotation.x = -Math.PI / 2;
  plane.position.y = -0.1;
  plane.receiveShadow = true;
  fullscreenScene.add(plane);

  fullscreenClock = new THREE.Clock();

  // Обработчик ресайза
  window.addEventListener('resize', handleFullscreenResize);
  // Обработчик ESC
  window.addEventListener('keydown', handleKeyDown);
};

// Загрузка модели для полноэкранного режима
const loadModelForFullscreen = async (model) => {
  if (!fullscreenScene) return;

  try {
    const loader = new FBXLoader();
    const fbx = await new Promise((resolve, reject) => {
      loader.load(`/models/${model.file}`, resolve, null, reject);
    });

    // Включаем тени и настраиваем материалы
    fbx.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        if (child.material) {
          child.material.metalness = 0.3;
          child.material.roughness = 0.6;
        }
      }
    });

    // Центрируем
    const box = new THREE.Box3().setFromObject(fbx);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    fbx.position.x = -center.x;
    fbx.position.y = -center.y + (size.y / 2);
    fbx.position.z = -center.z;

    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = (model.scale || 2) / maxDim;
    fbx.scale.multiplyScalar(scale);

    // Позиционируем
    if (model.position) {
      fbx.position.x += model.position.x;
      fbx.position.y += model.position.y;
      fbx.position.z += model.position.z;
    }

    fullscreenScene.add(fbx);

    // Настраиваем анимации
    if (fbx.animations && fbx.animations.length > 0) {
      fullscreenMixer = new THREE.AnimationMixer(fbx);
      fbx.animations.forEach(clip => {
        fullscreenMixer.clipAction(clip).play();
      });
    }

    // Настраиваем камеру
    const bbox = new THREE.Box3().setFromObject(fbx);
    const bboxSize = bbox.getSize(new THREE.Vector3());
    const bboxCenter = bbox.getCenter(new THREE.Vector3());
    
    const maxSize = Math.max(bboxSize.x, bboxSize.y, bboxSize.z);
    const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * fullscreenCamera.fov) / 360));
    const distance = fitHeightDistance * 1.5;
    
    fullscreenCamera.position.copy(bboxCenter);
    fullscreenCamera.position.x += distance;
    fullscreenCamera.position.y += distance * 0.5;
    fullscreenCamera.position.z += distance;
    fullscreenCamera.lookAt(bboxCenter);
    
    fullscreenControls.target.copy(bboxCenter);
    fullscreenControls.update();

    // Запускаем анимацию
    animateFullscreen();

  } catch (error) {
    console.error('Ошибка загрузки модели:', error);
  }
};

// Анимация полноэкранного режима
const animateFullscreen = () => {
  if (!fullscreenRenderer || !fullscreenScene || !fullscreenCamera) return;

  requestAnimationFrame(animateFullscreen);

  const delta = fullscreenClock.getDelta();
  
  if (fullscreenMixer) {
    fullscreenMixer.update(delta);
  }

  if (fullscreenControls) {
    fullscreenControls.update();
  }

  fullscreenRenderer.render(fullscreenScene, fullscreenCamera);
};

// Обработчик ресайза
const handleFullscreenResize = () => {
  if (!fullscreenCanvas.value || !fullscreenCamera || !fullscreenRenderer) return;
  
  fullscreenCamera.aspect = fullscreenCanvas.value.clientWidth / fullscreenCanvas.value.clientHeight;
  fullscreenCamera.updateProjectionMatrix();
  fullscreenRenderer.setSize(
    fullscreenCanvas.value.clientWidth,
    fullscreenCanvas.value.clientHeight
  );
};

// Обработчик клавиш
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && fullscreenModel.value) {
    closeFullscreen();
  }
  if (event.key === 'r' || event.key === 'R') {
    resetCamera();
  }
  if (event.key === 'w' || event.key === 'W') {
    toggleWireframe();
  }
  if (event.key === ' ') {
    toggleAutoRotate();
  }
};

// Функции управления
const toggleAutoRotate = () => {
  autoRotate.value = !autoRotate.value;
  if (fullscreenControls) {
    fullscreenControls.autoRotate = autoRotate.value;
  }
};

const resetCamera = () => {
  if (fullscreenControls && fullscreenModel.value) {
    // Здесь можно реализовать сброс камеры к исходной позиции
    fullscreenControls.reset();
  }
};

const toggleWireframe = () => {
  wireframe.value = !wireframe.value;
  if (fullscreenScene) {
    fullscreenScene.traverse((child) => {
      if (child.isMesh && child.material) {
        child.material.wireframe = wireframe.value;
      }
    });
  }
};

const closeFullscreen = () => {
  fullscreenModel.value = null;
  disposeFullscreen();
  
  // Удаляем обработчики
  window.removeEventListener('resize', handleFullscreenResize);
  window.removeEventListener('keydown', handleKeyDown);
};

// Очистка ресурсов полноэкранного режима
const disposeFullscreen = () => {
  if (fullscreenMixer) {
    fullscreenMixer.stopAllAction();
    fullscreenMixer = null;
  }
  
  if (fullscreenControls) {
    fullscreenControls.dispose();
  }
  
  if (fullscreenRenderer) {
    fullscreenRenderer.dispose();
  }
  
  // Очищаем сцену
  if (fullscreenScene) {
    fullscreenScene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(m => m.dispose());
        } else {
          object.material.dispose();
        }
      }
    });
    fullscreenScene.clear();
  }
};

// Инициализация
onMounted(() => {
  loadModelsConfig();
});

// Очистка
onUnmounted(() => {
  closeFullscreen();
  
  // Очищаем миниатюры
  models.value.forEach(model => {
    if (model.renderer) {
      model.renderer.dispose();
    }
  });
});
</script>

<style scoped>
.gallery-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
  color: white;
  padding: 20px;
}

/* Галерея */
.gallery-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
}

.gallery-header h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #4cc9f0, #4361ee);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.gallery-subtitle {
  color: #a0a0c0;
  margin-bottom: 20px;
}

.gallery-stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  font-size: 0.9rem;
  color: #8888aa;
}

/* Сетка моделей */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 25px;
  max-width: 1400px;
  margin: 0 auto;
}

.model-card {
  background: rgba(255, 255, 255, 0.07);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.model-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(100, 149, 237, 0.5);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.model-card.loading {
  opacity: 0.7;
}

.model-card.error {
  border-color: rgba(255, 50, 50, 0.5);
}

/* Миниатюра */
.model-thumbnail {
  position: relative;
  width: 100%;
  height: 250px;
  background: #0a0a14;
}

.model-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.thumbnail-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
}

.thumbnail-overlay.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  top: 0;
  background: rgba(0, 0, 0, 0.7);
}

.thumbnail-overlay.error {
  background: rgba(255, 50, 50, 0.2);
  color: #ff6b6b;
}

.thumbnail-overlay.info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.model-badge {
  display: flex;
  gap: 10px;
  align-items: center;
}

.poly-count {
  background: rgba(0, 0, 0, 0.5);
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: #4cc9f0;
}

.anim-badge {
  font-size: 1.2rem;
}

/* Информация о модели */
.model-card-info {
  padding: 20px;
}

.model-card-info h3 {
  margin: 0 0 10px 0;
  font-size: 1.3rem;
  color: #fff;
}

.model-description {
  color: #a0a0c0;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.4;
}

.model-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #8888aa;
}

.file-name {
  background: rgba(0, 0, 0, 0.3);
  padding: 3px 8px;
  border-radius: 10px;
  font-family: monospace;
}

.click-hint {
  color: #4cc9f0;
}

/* Полноэкранный режим */
.fullscreen-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  background: #0a0a14;
  display: none;
}

.fullscreen-view.active {
  display: flex;
  flex-direction: column;
}

.fullscreen-header {
  padding: 20px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.fullscreen-header h2 {
  margin: 0;
  color: white;
}

.model-description {
  margin: 5px 0 0 0;
  color: #a0a0c0;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 50, 50, 0.2);
  border-color: rgba(255, 50, 50, 0.5);
}

.close-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.fullscreen-content {
  flex: 1;
  position: relative;
}

.fullscreen-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.fullscreen-controls {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  background: rgba(0, 0, 0, 0.7);
  padding: 15px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
}

.control-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(100, 149, 237, 0.3);
  border-color: rgba(100, 149, 237, 0.5);
}

/* Прогресс загрузки */
.loading-progress {
  margin-top: 40px;
  text-align: center;
}

.progress-bar {
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin: 0 auto 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4cc9f0, #4361ee);
  transition: width 0.3s ease;
}

.loading-progress p {
  color: #d1d1e2;
  font-size: 0.9rem;
}

/* Спиннер */
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #4cc9f0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Адаптивность */
@media (max-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
  
  .fullscreen-controls {
    flex-wrap: wrap;
    justify-content: center;
    bottom: 20px;
    padding: 10px;
  }
  
  .gallery-header h1 {
    font-size: 2rem;
  }
}
</style>